import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dataFlowLog, summarizeCollection } from '../utils/dataFlowLogger';
import queryKeys from '../queries/queryKeys';
import useQueryScope from '../queries/useQueryScope';
import { ownsQueryScope } from '../queries/queryOwnership';

const defaultEventsContext = {
  events: [],
  loading: false,
  setEvents: () => {},
  fetchEvents: async () => {},
  createEvent: async () => {},
  removeEvent: () => {},
  updateEvent: () => {},
  upsertEventAttendee: () => {},
};

const EventsContext = createContext(defaultEventsContext);

export const useEventsContext = () => useContext(EventsContext);


// Query and service boundaries store EventSummary[]; undefined means not loaded.
const eventItems = (value) => {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) throw new TypeError('Events state must be an array.');
  return value;
};

const getEventId = (event) => event?._id || event?.id;

export const EventsProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { scope, isCurrent, enabled, hasProvider } = useQueryScope();
  const eventsQueryKey = useMemo(() => queryKeys.events.list({ scope: 'context', ...scope }), [scope]);
  const eventsQuery = useQuery({
    queryKey: eventsQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: [],
  });
  const events = useMemo(
    () => eventItems(eventsQuery.data),
    [eventsQuery.data],
  );

  const setEvents = useCallback((itemsOrUpdater, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : (!isCurrent() || (hasProvider && !enabled))) return;
    const targetKey = options.scope ? queryKeys.events.list({ scope: 'context', ...options.scope }) : eventsQueryKey;
    const currentItems = eventItems(queryClient.getQueryData(targetKey));
    const resolvedItems = typeof itemsOrUpdater === 'function'
      ? itemsOrUpdater(currentItems)
      : itemsOrUpdater;

    dataFlowLog('events-context.set.request', {
      next: typeof itemsOrUpdater === 'function' ? 'updater' : summarizeCollection(itemsOrUpdater),
    });
    queryClient.setQueryData(targetKey, eventItems(resolvedItems));
  }, [queryClient, eventsQueryKey, isCurrent, enabled, hasProvider]);

  const fetchEvents = useCallback(async () => {
    if (!isCurrent() || (hasProvider && !enabled)) return [];
    const currentItems = eventItems(queryClient.getQueryData(eventsQueryKey));
    queryClient.setQueryData(eventsQueryKey, currentItems);
    return currentItems;
  }, [queryClient, eventsQueryKey, isCurrent, enabled, hasProvider]);

  const createEvent = useCallback(async (event) => {
    setEvents((current) => [...current, event]);
    return event;
  }, [setEvents]);

  const removeEvent = useCallback((id) => {
    setEvents((current) => current.filter((event) => getEventId(event) !== id));
  }, [setEvents]);

  const updateEvent = useCallback((event) => {
    const updatedId = getEventId(event);
    if (!updatedId) return;

    setEvents((current) => {
      const idx = current.findIndex((item) => getEventId(item) === updatedId);
      if (idx === -1) return [event, ...current];

      const next = current.slice();
      next[idx] = { ...current[idx], ...event };
      return next;
    });
  }, [setEvents]);

  const upsertEventAttendee = useCallback((eventId, attendee, { remove = false } = {}) => {
    if (!eventId || !attendee) return;

    setEvents((current) => {
      const existing = current.find((item) => getEventId(item) === eventId);
      if (!existing) return current;

      const attendeeId = attendee?.refId || attendee?._id || attendee?.id || attendee;
      const currentAttendees = Array.isArray(existing.attendees) ? existing.attendees : [];
      const filteredAttendees = currentAttendees.filter((entry) => {
        const entryId = entry?.refId || entry?._id || entry?.id || entry;
        return String(entryId) !== String(attendeeId);
      });

      return current.map((event) => (
        getEventId(event) === eventId
          ? {
              ...existing,
              attendees: remove ? filteredAttendees : [...filteredAttendees, attendee],
            }
          : event
      ));
    });
  }, [setEvents]);

  const value = useMemo(() => ({
    events,
    queryKey: eventsQueryKey,
    loading: eventsQuery.isLoading,
    setEvents,
    fetchEvents,
    createEvent,
    removeEvent,
    updateEvent,
    upsertEventAttendee,
  }), [
    createEvent,
    events,
    eventsQueryKey,
    eventsQuery.isLoading,
    fetchEvents,
    removeEvent,
    setEvents,
    updateEvent,
    upsertEventAttendee,
  ]);

  useEffect(() => {
    dataFlowLog('events-context.updated', {
      loading: eventsQuery.isLoading,
      events: summarizeCollection(events),
    });
  }, [events, eventsQuery.isLoading]);

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
};
